import React from 'react';

interface CardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  isLoading?: boolean;
  error?: string | null;
  className?: string;
  accentColor?: string;
  icon?: React.ReactNode;
  headerAction?: React.ReactNode;
}

/**
 * Card component for wrapping analytics charts
 * Professional design with better visual hierarchy
 */
export function Card({
  title,
  subtitle,
  children,
  isLoading = false,
  error = null,
  className = '',
  accentColor = 'from-brand-electric-blue to-blue-600',
  icon,
  headerAction,
}: CardProps) {
  return (
    <div className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100/80 h-full flex flex-col ${className}`}>
      {/* Gradient accent bar at top */}
      <div className={`h-1.5 bg-gradient-to-r ${accentColor} flex-shrink-0`}></div>
      
      <div className="p-6 flex-1 flex flex-col">
        {/* Header with icon and action */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-start gap-3">
            {icon && (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-electric-blue/10 to-brand-dark-blue/5 flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 tracking-tight">{title}</h3>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{subtitle}</p>
              )}
            </div>
          </div>
          {headerAction && (
            <div className="flex-shrink-0">{headerAction}</div>
          )}
        </div>

        {/* Loading state - improved skeleton */}
        {isLoading && (
          <div className="flex-1 flex items-center justify-center min-h-[280px]">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full border-4 border-gray-100"></div>
                <div className="w-14 h-14 rounded-full border-4 border-brand-electric-blue border-t-transparent animate-spin absolute top-0 left-0"></div>
              </div>
              <p className="text-sm text-gray-400 font-medium">Loading data...</p>
            </div>
          </div>
        )}

        {/* Error state - improved design */}
        {error && (
          <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-red-800">Unable to load data</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
                <button className="mt-3 text-sm font-medium text-red-700 hover:text-red-800 underline underline-offset-2">
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && <div className="w-full flex-1">{children}</div>}
      </div>
    </div>
  );
}
